import { ProductRepaymentType } from 'domain/Product';
import { Nullable } from 'utils/types';
import { ExportTypes, Resources } from '../../CalculationId.types';
import { IResultsViewModel } from '../../utils';

export enum FIELDS {
  APPLICANT_1 = 'applicant1',
  APPLICANT_2 = 'applicant2',
  NUMBER_OF_APPLICANTS = 'numberOfApplicants',
  LOAN_DETAILS_PREFIX = 'loanDetails',
  LOAN_REQUESTED = 'loanRequested',
  MORTGAGE_PRODUCT = 'mortgageProduct',
  PRODUCT_REPAYMENT_TYPE = 'productRepaymentType',
  PROPERTY_VALUE = 'propertyValue',
  IS_PRIMARY_HOME = 'isPrimaryHome',
  POST_CODE = 'postCode',
  MONTHLY_LIVING_COSTS = 'monthlyLivingCosts',
  ANNUAL_COUNCIL_TAX = 'Annual council tax',
  ANNUAL_GROUND_RENT_SERVICE_CHARGE = 'Annual ground rent/service charge',
  APPLICANT_DATE_OF_BIRTH = 'dateOfBirth',
  APPLICANT_GENDER = 'gender',
  APPLICANT_MARITAL_STATUS = 'maritalStatus',
  APPLICANT_LIFE_STAGE = 'lifeStage',
  APPLICANT_RETIREMENT_AGE = 'retirementAge',
  CURRENT_INCOME_PREFIX = 'currentIncome',
  FUTURE_INCOME_PREFIX = 'futureIncome',
  EMPLOYED_GROSS_SALARY = 'employedGrossSalary',
  SELF_EMPLOYED_SALARY = 'selfEmployedSalary',
  BUSINESS_TYPE = 'businessType',
  MIN_MONTHLY_TAX_FREE_RETIRED_INCOME = 'minMonthlyTaxFreeRetiredIncome',
  HAS_TAX_FREE_INCOME_CONTINUING = 'hasTaxFreeIncomeContinuing',
  MIN_MONTHLY_TAXABLE_RETIRED_INCOME = 'minMonthlyTaxableRetiredIncome',
  HAS_TAXABLE_INCOME_CONTINUING = 'hasTaxableIncomeContinuing',
  OTHER_INCOME_PREFIX = 'otherIncome',
  INVESTMENT_AND_SAVINGS = 'investmentAndSavings',
  RENTAL_INCOME = 'rentalIncome',
  MAINTENANCE_INCOME = 'maintenanceIncome',
  FOSTERING_INCOME = 'fosteringIncome',
  BENEFIT_INCOME = 'benefitIncome',
  GUARANTEED_BENEFIT = 'guaranteedBenefit',
  GUARANTEED_MAINTENANCE = 'guaranteedMaintenance',
  LIFE_EVENTS_PREFIX = 'lifeEvents',
  INVESTMENT_PORTFOLIO_VALUE = 'investmentPortfolioValue',
  VALUE_OF_ASSET_AT_RETIREMENT = 'valueOfAssetAtRetirement',
  MORTGAGE_PROTECTION_MONTHLY_PAYMENT = 'mortgageProtectionMonthlyPayment',
  LIFE_INSURANCE_LUMP_SUM = 'lifeInsuranceLumpSum',
  LIFE_INSURANCE_PREMIUM = 'lifeInsurancePremium',
  RELATED_TO_EMPLOYED_GROSS_SALARY = 'employedGrossSalaryRelated',
  RELATED_TO_SELF_EMPLOYED_SALARY = 'selfEmployedSalaryRelated',
  BONUS_INCOME = 'bonusIncome',
  IS_GUARANTEED_BONUS_INCOME = 'isGuaranteedBonusIncome',
  COMMISSION_INCOME = 'commissionIncome',
  IS_GUARANTEED_COMMISSION_INCOME = 'isGuaranteedCommissionIncome',
  OVERTIME_INCOME = 'overtimeIncome',
  IS_GUARANTEED_OVERTIME_INCOME = 'isGuaranteedOvertimeIncome',
  SHIFT_ALLOWANCE_INCOME = 'shiftAllowanceIncome',
  IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME = 'isGuaranteedShiftAllowanceIncome',
  DIVIDENTS_INCOME = 'dividentsIncome',
  CURRENT_PENSION_PREFIX = 'currentPension',
  FUTURE_PENSION_PREFIX = 'futurePension',
  STATE_PENSION = 'statePension',
  DEFINED_BENEFIT = 'definedBenefit',
  PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT = 'perPassedOnDeathNextToDefB',
  DEFINED_CONTRIBUTION = 'definedContribution',
  PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION = 'perPassedOnDeathNextToDefC',
  ANNUITY = 'annuity',
  PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY = 'perPassedOnDeathNextToAnnuity',
  PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT = 'perTransferredOnDeathUnderDefB',
  REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT = 'repaymentLumpSumUnderDefB',
  USE_OF_FUNDS = 'useOfFunds',
  ONGOING_CONTRIBUTIONS = 'ongoingContributions',
  PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION = 'perTransferredOnDeathUnderToDefC',
  LUMP_SUM_DRAWDOWN = 'lumpSumDrawdown',
  REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION = 'repaymentLumpSumUnderDefC',
  LIABILITY_PREFIX = 'liabilities',
  LIABILITY_TYPE = 'type',
  LIABILITY_CREDIT_CARD_BALANCE = 'creditCardBalance',
  LIABILITY_AMOUNT = 'amount',
  LIABILITY_COMMITMENT_END_DATE = 'commitmentEnds',
  LIABILITY_IS_CLEARED_BEFORE_LOAN_COMPLETES = 'isClearedBeforeLoanCompletes',
  TERM_LENGTH = 'termLength',
}

export enum FIELDS_QUESTIONS {
  LOAN_REQUESTED = 'Loan requested',
  MORTGAGE_PRODUCT = 'Mortgage product',
  PRODUCT_REPAYMENT_TYPE = 'Repayment type',
  TERM_LENGTH = 'Term length',
  PROPERTY_VALUE = 'Property value',
  IS_PRIMARY_HOME = 'Primary Residence',
  POST_CODE = 'Postcode',
  MONTHLY_LIVING_COSTS = 'Monthly living costs - not currently used',
  ANNUAL_COUNCIL_TAX = 'Annual council tax',
  ANNUAL_GROUND_RENT_SERVICE_CHARGE = 'Annual ground rent/service charge',
  APPLICANT_DATE_OF_BIRTH = 'Date of birth',
  APPLICANT_GENDER = 'Gender',
  APPLICANT_MARITAL_STATUS = 'Marital status - not currently used',
  APPLICANT_LIFE_STAGE = 'Life stage',
  APPLICANT_RETIREMENT_AGE = 'Retirement age',
  EMPLOYED_GROSS_SALARY = `Employed gross salary - 'Not retired' only - applies until retirement`,
  SELF_EMPLOYED_SALARY = `Self-employed income - 'Not retired' only - applies until retirement`,
  BUSINESS_TYPE = 'Business type',
  MIN_MONTHLY_TAX_FREE_RETIRED_INCOME = 'Minimum tax-free retired income (Benefits & Maintenance)',
  MIN_MONTHLY_TAXABLE_RETIRED_INCOME = 'Future income (only to be used for employed and self-employed income)',
  INVESTMENT_AND_SAVINGS = 'Investment & savings - applies until mortality',
  RENTAL_INCOME = 'Rental income - applies until mortality, transfers to surviving applicant',
  MAINTENANCE_INCOME = 'Maintenance income - applies until mortality',
  FOSTERING_INCOME = 'Fostering income - applies until Age 75',
  BENEFIT_INCOME = 'Benefit income - guaranteed applies until mortality, non-guaranteed to retirement',
  INVESTMENT_PORTFOLIO_VALUE = 'Investment portfolio value - not currently used',
  VALUE_OF_ASSET_AT_RETIREMENT = `Value of assets at retirement - 'Not retired' only - applies from retirement age`,
  MORTGAGE_PROTECTION_MONTHLY_PAYMENT = 'Mortgage protection monthly payments - not currently used',
  LIFE_ASSURANCE_LUMP_SUM = 'Life insurance lump sum',
  LIFE_ASSURANCE_PREMIUM = 'Life insurance monthly premium',
  BONUS_INCOME = 'Bonus',
  COMMISSION_INCOME = 'Commission',
  OVERTIME_INCOME = 'Overtime',
  SHIFT_ALLOWANCE_INCOME = 'Shift allowance',
  DIVIDENTS_INCOME = `Dividends - 'Not retired' only - applies until retirement`,
  STATE_PENSION = 'State Pension - used from State Pension Age',
  DEFINED_BENEFIT = 'Defined benefit - used from start',
  DEFINED_BENEFIT_FUTURE = 'Defined benefit - used from retirement age',
  PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT = '% passed on death',
  DEFINED_CONTRIBUTION = 'Defined contribution - used from start',
  DEFINED_CONTRIBUTION_FUTURE = 'Defined contribution - used from retirement age',
  PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION = '% passed on death',
  ANNUITY = 'Annuity - used from start',
  PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY = '% passed on death',
  PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT = '% passed on death',
  REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT = 'Repayment lump sum',
  USE_OF_FUNDS = 'Use of funds',
  ONGOING_CONTRIBUTIONS = 'Ongoing monthly contributions',
  PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION = '% passed on death',
  LUMP_SUM_DRAWDOWN = 'Lump sum drawdown',
  REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION = 'Repayment lump sum',
  LIABILITY_TYPE = 'type',
  LIABILITY_CREDIT_CARD_BALANCE = 'Credit/Store card balance',
  LIABILITY_AMOUNT = 'Amount',
  LIABILITY_COMMITMENT_END_DATE = 'Commitment end data',
  LIABILITY_IS_CLEARED_BEFORE_LOAN_COMPLETES = 'Is cleared',
  INCOME_CONTINUING = 'Income continuing',
}

type OtherIncome = {
  [FIELDS.INVESTMENT_AND_SAVINGS]: Nullable<number>;
  [FIELDS.RENTAL_INCOME]: Nullable<number>;
  [FIELDS.MAINTENANCE_INCOME]: Nullable<number>;
  [FIELDS.FOSTERING_INCOME]: Nullable<number>;
  [FIELDS.BENEFIT_INCOME]: Nullable<number>;
  [FIELDS.GUARANTEED_BENEFIT]: Nullable<boolean>;
  [FIELDS.GUARANTEED_MAINTENANCE]: Nullable<boolean>;
};

type CurrentIncome = {
  [FIELDS.EMPLOYED_GROSS_SALARY]: Nullable<number>;
  [FIELDS.SELF_EMPLOYED_SALARY]: Nullable<number>;
  [FIELDS.BUSINESS_TYPE]: Nullable<string>;
  [FIELDS.OTHER_INCOME_PREFIX]: OtherIncome;
  [FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY]: {
    [FIELDS.BONUS_INCOME]: Nullable<number>;
    [FIELDS.COMMISSION_INCOME]: Nullable<number>;
    [FIELDS.OVERTIME_INCOME]: Nullable<number>;
    [FIELDS.SHIFT_ALLOWANCE_INCOME]: Nullable<number>;
    [FIELDS.IS_GUARANTEED_BONUS_INCOME]: Nullable<boolean>;
    [FIELDS.IS_GUARANTEED_COMMISSION_INCOME]: Nullable<boolean>;
    [FIELDS.IS_GUARANTEED_OVERTIME_INCOME]: Nullable<boolean>;
    [FIELDS.IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME]: Nullable<boolean>;
  };
  [FIELDS.RELATED_TO_SELF_EMPLOYED_SALARY]: {
    [FIELDS.DIVIDENTS_INCOME]: Nullable<number>;
  };
};

type FutureIncome = {
  [FIELDS.MIN_MONTHLY_TAXABLE_RETIRED_INCOME]: Nullable<number>;
  [FIELDS.HAS_TAXABLE_INCOME_CONTINUING]: Nullable<boolean>;
  [FIELDS.MIN_MONTHLY_TAX_FREE_RETIRED_INCOME]: Nullable<number>;
  [FIELDS.HAS_TAX_FREE_INCOME_CONTINUING]: Nullable<boolean>;
};

type CurrentPension = {
  [FIELDS.STATE_PENSION]: Nullable<number>;
  [FIELDS.DEFINED_BENEFIT]: Nullable<number>;
  [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_BENEFIT]: Nullable<number>;
  [FIELDS.DEFINED_CONTRIBUTION]: Nullable<number>;
  [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_DEF_CONTRIBUTION]: Nullable<number>;
  [FIELDS.ANNUITY]: Nullable<number>;
  [FIELDS.PER_PASSED_ON_DEATH_NEXT_TO_ANNUITY]: Nullable<number>;
};

type FuturePension = {
  [FIELDS.STATE_PENSION]: Nullable<number>;
  [FIELDS.DEFINED_BENEFIT]: Nullable<number>;
  [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_BENEFIT]: Nullable<number>;
  [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_BENEFIT]: Nullable<number>;
  [FIELDS.USE_OF_FUNDS]: Nullable<string>;
  [FIELDS.DEFINED_CONTRIBUTION]: Nullable<number>;
  [FIELDS.ONGOING_CONTRIBUTIONS]: Nullable<number>;
  [FIELDS.PER_TRANSFERRED_ON_DEATH_UNDER_DEF_CONTRIBUTION]: Nullable<number>;
  [FIELDS.LUMP_SUM_DRAWDOWN]: Nullable<number>;
  [FIELDS.REPAYMENT_LUMP_SUM_UNDER_DEFINED_CONTRIBUTION]: Nullable<number>;
};

type LifeEvents = {
  [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: Nullable<number>;
  [FIELDS.VALUE_OF_ASSET_AT_RETIREMENT]: Nullable<number>;
  [FIELDS.MORTGAGE_PROTECTION_MONTHLY_PAYMENT]: Nullable<number>;
  [FIELDS.LIFE_INSURANCE_LUMP_SUM]: Nullable<number>;
  [FIELDS.LIFE_INSURANCE_PREMIUM]: Nullable<number>;
};

type Liability = {
  [FIELDS.LIABILITY_TYPE]: string;
  [FIELDS.LIABILITY_CREDIT_CARD_BALANCE]: Nullable<number>;
  [FIELDS.LIABILITY_AMOUNT]: Nullable<number>;
  [FIELDS.LIABILITY_COMMITMENT_END_DATE]: Nullable<string>;
  [FIELDS.LIABILITY_IS_CLEARED_BEFORE_LOAN_COMPLETES]: Nullable<boolean>;
};

type Applicant = {
  [FIELDS.APPLICANT_DATE_OF_BIRTH]: Nullable<string>;
  [FIELDS.APPLICANT_GENDER]: Nullable<string>;
  [FIELDS.APPLICANT_MARITAL_STATUS]: Nullable<string>;
  [FIELDS.APPLICANT_LIFE_STAGE]: Nullable<string>;
  [FIELDS.APPLICANT_RETIREMENT_AGE]: Nullable<number>;
  [FIELDS.CURRENT_INCOME_PREFIX]: CurrentIncome;
  [FIELDS.FUTURE_INCOME_PREFIX]: FutureIncome;
  [FIELDS.LIFE_EVENTS_PREFIX]: LifeEvents;
  [FIELDS.CURRENT_PENSION_PREFIX]: CurrentPension;
  [FIELDS.FUTURE_PENSION_PREFIX]: FuturePension;
  [FIELDS.LIABILITY_PREFIX]: Array<Liability>;
};

export type ApplicantKey = 'applicant1' | 'applicant2';

export type FormValues = {
  [FIELDS.NUMBER_OF_APPLICANTS]: number;
  [FIELDS.LOAN_DETAILS_PREFIX]: {
    [FIELDS.LOAN_REQUESTED]: Nullable<number>;
    [FIELDS.PROPERTY_VALUE]: Nullable<number>;
    [FIELDS.IS_PRIMARY_HOME]: Nullable<string>;
    [FIELDS.POST_CODE]: Nullable<string>;
    [FIELDS.MONTHLY_LIVING_COSTS]: Nullable<number>;
    [FIELDS.MORTGAGE_PRODUCT]: Nullable<string>;
    [FIELDS.ANNUAL_COUNCIL_TAX]: Nullable<number>;
    [FIELDS.ANNUAL_GROUND_RENT_SERVICE_CHARGE]: Nullable<number>;
    [FIELDS.TERM_LENGTH]: Nullable<number>;
    [FIELDS.PRODUCT_REPAYMENT_TYPE]: Nullable<ProductRepaymentType>;
  };
  [FIELDS.APPLICANT_1]: Applicant;
  [FIELDS.APPLICANT_2]: Nullable<Applicant>;
};

export type Props = {
  onSubmit: (formValues: FormValues) => void;
  initialValues: FormValues;
  results: IResultsViewModel;
  resources: Resources;
  onExportClick: (exportType: ExportTypes) => void;
  error: any;
};

export enum APPLICANTS_TYPES {
  SINGLE = 'single',
  JOINT = 'joint',
}

export type ApplicantFormType = NonNullable<FormValues[ApplicantKey]>;
